import React from "react";
import {
  CheckCircle2,
  Phone,
  ClipboardCheck,
  Users,
  Wallet,
  ShieldCheck,
} from "lucide-react";

const eligibility = [
  "Alberta resident with a valid Alberta Health Care card",
  "Care needs can be safely met at home",
  "Approved by an AHS case manager following assessment",
  "Open to Albertans of any age — no private insurance required",
];

const covered = [
  {
    icon: Users,
    title: "Personal Care",
    text: "Bathing, dressing, mobility transfers, feeding, medication support and other daily living tasks.",
  },
  {
    icon: ClipboardCheck,
    title: "Homemaking",
    text: "Light housekeeping and tasks directly tied to safe living at home.",
  },
  {
    icon: ShieldCheck,
    title: "Respite Care",
    text: "Temporary, scheduled relief for family or informal caregivers.",
  },
];

const steps = [
  {
    n: "1",
    title: "Call 811 for an AHS Assessment",
    text: "Request a home care assessment from Alberta Health Services (or speak with your existing case manager).",
  },
  {
    n: "2",
    title: "Get Approved & Choose Aegis Care",
    text: "Once your hours are approved under CDHCI, select us as your approved provider.",
  },
  {
    n: "3",
    title: "We Deliver Care & Bill Directly",
    text: "We provide your services and submit claims directly to Alberta Blue Cross — you don't pay out-of-pocket for covered hours.",
  },
];

export const CDHCISection = () => {
  return (
    <section
      id="cdhci"
      data-testid="cdhci-section"
      className="section-padding bg-white"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span
              data-testid="cdhci-badge"
              className="inline-block px-3 py-1 rounded-full bg-[#FFF1E6] text-[#C2410C] text-xs uppercase tracking-widest font-semibold"
            >
              AHS Approved Program
            </span>
            <h2
              data-testid="cdhci-heading"
              className="font-display text-3xl md:text-5xl font-semibold mt-4 aegis-navy-text"
            >
              Client-Directed Home Care (CDHCI)
            </h2>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              CDHCI is an Alberta Health Services program that lets eligible
              Albertans receive publicly-funded home care from an approved
              provider. The provider bills <strong>Alberta Blue Cross</strong>{" "}
              directly — so you don't have to pay upfront and seek reimbursement
              later.
            </p>
            <div className="mt-7 p-5 rounded-xl bg-[#FFF7EE] border border-[#FBD9B4]">
              <div className="flex items-start gap-3">
                <Wallet size={22} className="text-[#C2410C] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold aegis-navy-text">
                    No out-of-pocket for approved hours
                  </div>
                  <p className="text-sm text-gray-700 mt-1">
                    AHS authorizes a set number of care hours. We deliver them
                    and invoice Alberta Blue Cross on your behalf.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="tel:811"
                data-testid="cdhci-cta-811"
                className="btn-primary"
              >
                <Phone size={16} />
                Call 811 for AHS Assessment
              </a>
              <a
                href="#booking"
                data-testid="cdhci-cta-book"
                className="btn-secondary"
              >
                Talk to Aegis Care
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <div
              data-testid="cdhci-eligibility"
              className="rounded-2xl border border-gray-200 p-7"
            >
              <h3 className="font-display text-xl font-semibold aegis-navy-text">
                Who qualifies?
              </h3>
              <ul className="mt-5 space-y-3">
                {eligibility.map((e) => (
                  <li key={e} className="flex gap-3 text-gray-700">
                    <CheckCircle2
                      size={20}
                      className="text-[#3D7A73] flex-shrink-0 mt-0.5"
                    />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="cdhci-services" className="grid sm:grid-cols-3 gap-4">
              {covered.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.title}
                    className="rounded-2xl bg-[#F4F7F6] p-5"
                  >
                    <div className="w-10 h-10 rounded-lg bg-white text-[#2A5C56] flex items-center justify-center mb-3">
                      <Icon size={18} />
                    </div>
                    <div className="font-display font-semibold aegis-navy-text">
                      {c.title}
                    </div>
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                      {c.text}
                    </p>
                  </div>
                );
              })}
            </div>

            <div
              data-testid="cdhci-steps"
              className="rounded-2xl aegis-navy-bg text-white p-7"
            >
              <h3 className="font-display text-xl font-semibold">
                How to get started in 3 steps
              </h3>
              <ol className="mt-5 space-y-5">
                {steps.map((s) => (
                  <li key={s.n} className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-[#3D7A73] flex items-center justify-center font-display font-bold flex-shrink-0">
                      {s.n}
                    </div>
                    <div>
                      <div className="font-semibold">{s.title}</div>
                      <p className="text-white/75 text-sm mt-1 leading-relaxed">
                        {s.text}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="mt-6 text-xs text-white/60">
                Source: Alberta Health Services & Alberta Blue Cross — Client-
                Directed Home Care Invoicing program.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
